/**
 * fillModelFeed.js — the FEED that bridges STORED segment data → the fill MODEL
 * (EW-003 u1b, ISP-9999, flag REACT_APP_SEGMENT_ARC_FILLS, default OFF).
 *
 * u1 delivered the two dual-surface derivers (toWedgePatternProps / toFrameStyle)
 * and wired createPie's wedge to consume `d.data.fill` — but NOTHING populated
 * `d.data.fill` from real segment data. Saved segments only carry:
 *   · the V2 blob  `settings.fill`                (WL-012, camelCase model)
 *   · LEGACY fields `settings.{bgWallpaper,segmentBgColor,currentBg,isPattern}`
 *     (what the currently-live SegmentView renderer reads; written by
 *     legacyFieldsForFill on every fill commit)
 *
 * This module is the missing bridge, in three pure pieces:
 *
 *   1. legacyFieldsToFillModel(src)  — reconstruct a fill MODEL from the LEGACY
 *      fields alone (for segments saved BEFORE the V2 blob existed). Inverse of
 *      legacyFieldsForFill.
 *   2. fillToModelFields(fill) / modelFieldsToFill(fields) — the ADDITIVE
 *      snake_case model fields the builder DUAL-WRITES when the flag is ON (BE-
 *      friendly flat scalars: fill_type, fill_src, fill_poster, fill_focal_x/y,
 *      fill_zoom, fill_mode). Legacy fields are STILL written alongside → zero
 *      regression when the flag is OFF (the model fields are simply ignored).
 *   3. resolveSegmentFill(src) — the ONE resolver the wheel-data feed calls:
 *      picks the richest available source (V2 blob > snake model fields >
 *      legacy fields) and returns a normalised fill model, or null when the
 *      segment has no image/gif/pattern fill to model.
 *   4. hydrateChartFill(chart, enabled) — map wheel chart items → attach a
 *      top-level `fill` (what createPie's wedgeFillProps reads). Flag OFF (or
 *      enabled=false) returns the SAME array reference → byte-identical render.
 *
 * Pure: no React, no redux, no DOM, no I/O. All geometry/crop lives downstream
 * in segmentFillSurfaces; this module only shuffles the model shape around.
 */

import { BG_IMAGE, BG_GRADIENT_CSS } from "./legacyFillFields";
// W1-1: the ONE casing-agnostic reader. `pickSetting(s, "currentBg")` is exactly
// the old `s.currentBg ?? s.current_bg` (camelCase wins over snake_case) — same
// precedence, now via the shared accessor so no raw snake/camel key is spelled
// here. The snake-only `fill_*` model fields below stay raw (they are an additive
// snake dual-write contract, never casing-variant — see modelFieldsToFill).
import { pickSetting } from "../../../../../utils/segmentSettingsAccessor";
// review-1008-Frontend-6683: currentBg===BG_GRADIENT_CSS is ambiguous — a saved
// Gradient and a saved Pattern write the IDENTICAL legacy shape (segmentBgColor:
// css, currentBg: 3; see legacyFillFields.js's gradient/pattern branches). The
// same ambiguity exists at the WEDGE-paint layer and is already resolved there
// (segmentFillSurfaces.js's gradientSpecOf: a `url(...)` tile is a pattern,
// anything else is a gradient) — reuse the SAME inverse parser here so the EDIT
// PANEL agrees with what the wedge actually paints.
import { parseGradientCss } from "../../../../gradientBuilder/gradientCss";
import { stripConicLoopStops } from "./segmentFill";

const clamp01 = (n, dflt = 0.5) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return dflt;
  return Math.min(1, Math.max(0, x));
};

const clampZoom = (z) => {
  const x = Number(z);
  if (!Number.isFinite(x)) return 1;
  return Math.min(4, Math.max(1, x));
};

/** A url ending in .gif (ignoring query/hash) is treated as an animated GIF. */
const looksLikeGif = (url) =>
  typeof url === "string" && /\.gif(\?|#|$)/i.test(url.trim());

const VALID_MODES = ["cover", "contain", "tile"];
const normMode = (m) => (VALID_MODES.includes(m) ? m : "cover");

/* -------------------------------------------------------------------------- */
/* 1. LEGACY fields → fill model (inverse of legacyFieldsForFill)             */
/* -------------------------------------------------------------------------- */

/**
 * Reconstruct a fill MODEL from a segment's LEGACY settings fields, so a segment
 * saved before the V2 model blob existed still renders through the model when the
 * flag is ON.
 *
 * Mapping (mirror of legacyFieldsForFill):
 *   currentBg === BG_IMAGE (2)        → image | gif  (gif by .gif extension)
 *   currentBg === BG_GRADIENT_CSS (3) → gradient (a <kind>-gradient(...) css,
 *                                        parsed back into a builder state) |
 *                                        pattern (anything else, carrying the
 *                                        raw css — typically a url(...) tile)
 *   otherwise                         → null (color has no wedge pattern; the
 *                                        wedge falls back to d.data.color)
 *
 * CASING (ISP-9999 gif-hover-fill-casing): the currently-live wallpaper path
 * (legacyFieldsForFill) WRITES these fields in camelCase — `bgWallpaper` /
 * `currentBg` / `segmentBgColor` — but the API returns a segment's `settings` in
 * SNAKE_CASE — `bg_wallpaper` / `current_bg` / `segment_bg_color` (confirmed live:
 * GET /api/v2/wheel/<id>/ returns snake `bg_wallpaper`, `current_bg`, …). Reading
 * ONLY camelCase made every gif/image fill saved via the legacy wallpaper path
 * resolve to `null` on real (reloaded/card) data → its wedge never got a fill
 * MODEL → `d.data.fill.type` was never "gif" → createPie's `mountGifHoverWedges`
 * skipped it → the GIF stayed FROZEN on hover (Gordon's exact symptom). Read BOTH
 * casings so the resolver works on the in-memory (camelCase, freshly-picked) shape
 * AND the API (snake_case, reloaded) shape. Same casing class as PR #2117's palette
 * INERT bug; fixed here at the resolver (independent of #2117, no shared file).
 *
 * @param {object} src a settings-like object with the legacy fill fields in EITHER
 *   casing (`bgWallpaper`|`bg_wallpaper`, `currentBg`|`current_bg`,
 *   `segmentBgColor`|`segment_bg_color`, `isPattern`|`is_pattern`)
 * @returns {object|null} a fill model, or null when there is nothing to model
 */
export const legacyFieldsToFillModel = (src) => {
  const s = src || {};
  const currentBg = Number(pickSetting(s, "currentBg"));

  if (currentBg === BG_IMAGE) {
    const url = pickSetting(s, "bgWallpaper");
    if (!url) return null;
    const focus = { x: 0.5, y: 0.5, zoom: 1 };
    if (looksLikeGif(url)) {
      return { type: "gif", gif: { url, focus, mode: "cover" } };
    }
    return { type: "image", image: { url, focus, mode: "cover" } };
  }

  if (currentBg === BG_GRADIENT_CSS) {
    const css = pickSetting(s, "segmentBgColor");
    if (!css) return null;
    // A CSS url("data:...") tile is a pattern; a <linear|radial|conic>-gradient(...)
    // css is a real Gradient fill (same disambiguation segmentFillSurfaces.js's
    // gradientSpecOf already applies for the wedge paint — see import note above).
    // Only a url()-free string is tried as a gradient, so an actual pattern whose
    // css happens to carry no tile (rare/malformed) still falls through to the
    // pattern model below rather than being misparsed.
    if (!/url\(/i.test(css)) {
      const gradient = parseGradientCss(css);
      // A saved Segment conic css is the LOOPED stop list (closing first-colour stop
      // + rescaled positions, see buildConicLoopStops) — strip it back to the user's
      // stops, else every edit/save/reload adds one more stop.
      if (gradient && gradient.type === "conic") {
        gradient.stops = stripConicLoopStops(gradient.stops);
      }
      if (gradient) return { type: "gradient", gradient };
    }
    return { type: "pattern", pattern: { css } };
  }

  return null;
};

/* -------------------------------------------------------------------------- */
/* 2. ADDITIVE snake_case model fields (dual-write + reload)                  */
/* -------------------------------------------------------------------------- */

/**
 * Flatten a fill model into ADDITIVE snake_case scalar fields for the builder to
 * DUAL-WRITE (alongside the legacy fields) when the flag is ON. Flat scalars are
 * BE-friendly and forward-safe: an older client/renderer ignores unknown keys.
 *
 * Only image / gif / pattern carry crop state worth flattening; color / gradient
 * live fully in the V2 blob + legacy css, so they emit just `fill_type`.
 *
 * @param {object} fill the fill model
 * @returns {object} snake_case fields (never null; empty-ish for no-media fills)
 */
export const fillToModelFields = (fill) => {
  const f = fill || {};
  const type = f.type;
  if (!type) return {};

  const out = { fill_type: type };

  if (type === "image" || type === "gif") {
    const media = (type === "gif" ? f.gif : f.image) || {};
    const focus = media.focus || {};
    if (media.url) out.fill_src = media.url;
    if (media.poster) out.fill_poster = media.poster;
    out.fill_focal_x = clamp01(focus.x);
    out.fill_focal_y = clamp01(focus.y);
    out.fill_zoom = clampZoom(focus.zoom);
    out.fill_mode = normMode(media.mode);
  } else if (type === "pattern") {
    const p = f.pattern || {};
    if (p.css) out.fill_pattern_css = p.css;
    if (p.id) out.fill_pattern_id = p.id;
  } else if (type === "color") {
    if (f.color) out.fill_color = f.color;
  }

  return out;
};

/**
 * Reconstruct a fill model from the snake_case model fields (reload path). Inverse
 * of fillToModelFields. Returns null when the fields describe no image/gif/pattern
 * fill (so the caller falls through to the legacy fields / d.data.color).
 *
 * @param {object} fields snake_case fields
 * @returns {object|null} a fill model or null
 */
export const modelFieldsToFill = (fields) => {
  const s = fields || {};
  const type = s.fill_type;
  if (!type) return null;

  if (type === "image" || type === "gif") {
    const url = s.fill_src;
    if (!url) return null;
    const media = {
      url,
      focus: {
        x: clamp01(s.fill_focal_x),
        y: clamp01(s.fill_focal_y),
        zoom: clampZoom(s.fill_zoom),
      },
      mode: normMode(s.fill_mode),
    };
    if (s.fill_poster) media.poster = s.fill_poster;
    return type === "gif" ? { type: "gif", gif: media } : { type: "image", image: media };
  }

  if (type === "pattern") {
    if (!s.fill_pattern_css && !s.fill_pattern_id) return null;
    const pattern = {};
    if (s.fill_pattern_css) pattern.css = s.fill_pattern_css;
    if (s.fill_pattern_id) pattern.id = s.fill_pattern_id;
    return { type: "pattern", pattern };
  }

  if (type === "color") {
    return s.fill_color ? { type: "color", color: s.fill_color } : null;
  }

  return null;
};

/* -------------------------------------------------------------------------- */
/* 3. The ONE resolver (richest source wins)                                  */
/* -------------------------------------------------------------------------- */

const isImageLike = (fill) =>
  fill && (fill.type === "image" || fill.type === "gif" || fill.type === "pattern");

// A V2 blob is only useful to the WEDGE when it carries a concrete media/pattern
// source. A color/gradient blob has no wedge pattern (the arc paints d.data.color
// / a css background), so we treat it as "no wedge model" and fall through.
const blobHasWedgeSource = (fill) => {
  if (!isImageLike(fill)) return false;
  if (fill.type === "pattern") return !!(fill.pattern && (fill.pattern.css || fill.pattern.id));
  const media = (fill.type === "gif" ? fill.gif : fill.image) || {};
  return !!media.url;
};

/**
 * Resolve the fill MODEL for a segment from whatever it has stored, richest
 * source first:
 *   1. the V2 blob `settings.fill` (WL-012 camelCase model) — when it carries a
 *      concrete wedge source,
 *   2. the additive snake_case model fields,
 *   3. the LEGACY fields.
 * Reads both `src.settings.*` and top-level `src.*` (chart items carry legacy
 * fields at either level across the app's dual data paths).
 *
 * @param {object} src a segment or chart item (with optional `.settings`)
 * @returns {object|null} a normalised fill model, or null when nothing to model
 */
export const resolveSegmentFill = (src) => {
  if (!src) return null;
  const settings = src.settings || {};
  // Merged view: settings wins, top-level is the fallback (both paths seen live).
  const merged = { ...src, ...settings };

  // 1. V2 blob (settings.fill or top-level fill)
  const blob = settings.fill || src.fill;
  if (blobHasWedgeSource(blob)) return blob;

  // 2. snake_case model fields
  const fromFields = modelFieldsToFill(merged);
  if (fromFields) return fromFields;

  // 3. legacy fields
  return legacyFieldsToFillModel(merged);
};

/* -------------------------------------------------------------------------- */
/* 4. Wheel-data hydration                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Attach a top-level `fill` (the model createPie.wedgeFillProps reads) to every
 * chart item that resolves one. Flag OFF (enabled=false) is a pure pass-through:
 * the SAME array reference is returned → the wheel render is byte-identical.
 *
 * Never mutates the input items; items that already carry a `fill` are left as-is
 * (an explicit model always wins). Non-array input is returned unchanged.
 *
 * @param {Array<object>} chart the wheel chart items
 * @param {boolean} enabled the REACT_APP_SEGMENT_ARC_FILLS flag
 * @returns {Array<object>} chart items, hydrated when enabled
 */
export const hydrateChartFill = (chart, enabled) => {
  if (!enabled) return chart;
  if (!Array.isArray(chart)) return chart;
  return chart.map((item) => {
    if (!item || item.fill) return item;
    const fill = resolveSegmentFill(item);
    return fill ? { ...item, fill } : item;
  });
};

const fillModelFeed = {
  legacyFieldsToFillModel,
  fillToModelFields,
  modelFieldsToFill,
  resolveSegmentFill,
  hydrateChartFill,
};
export default fillModelFeed;
